import { getRouterState, IStoreState } from "../../rootReducer";
import { isAccessTokenExpired, ITokenRequestData } from "./utils";
import CodeMessageError from "../../../error/CodeMessageError";
import { putAuthTokenUpdateFulfilled } from "./reducer";
import config from "../../../config";
import { IExtraArgument, IGcpAppDispatch } from "../../store";
import { AxiosRequestConfig, AxiosResponse } from "axios";

const ERROR_LOGIN_SERVICE_API_SCHEMA_INVALID =
  "LOGIN_SERVICE_API_SCHEMA_INVALID";

interface ILoginServiceApiMeResponse {
  accessToken: string;
}

// This is needed to be declared explicitly
// to reuse this in mock
export type IPerformSignedRequest = (
  axiosRequestConfig: AxiosRequestConfig,
  customerId: string,
  siteId: string,
) => (
  dispatch: IGcpAppDispatch,
  getState: () => IStoreState,
  { axios }: IExtraArgument,
) => Promise<any>;

export const ensureUserLoggedIn =
  () =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
  ): Promise<[true, string] | [false, undefined]> => {
    try {
      const routerState = getRouterState(getState());
      const { customerId, siteId } = routerState.data;
      const token = await dispatch(ensureAccessToken(customerId, siteId));
      return [true, token];
    } catch (e) {
      // This usually should not happen because user is navigated to GCP App from
      // Backoffice, but it potentially can happen if user is navigated from e-mail.
      // In my case it happened when Omega recovered wrong backup on dev 2020.01.08
      // and the customer that I was working in was lost.
      if (
        ["NO_CUSTOMER_FOUND", "USER_HAS_NO_ROLE_IN_CUSTOMER"].includes(
          e.response.data.code,
        )
      ) {
        throw new Error(
          "You have no access to this customer or it was removed",
        );
      }
      return [false, undefined];
    }
  };

let tokenFetchPromise: Promise<AxiosResponse<ILoginServiceApiMeResponse>> =
  null;

export const ensureAccessToken =
  (customerId: string, siteId: string) =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { axios }: IExtraArgument,
  ) => {
    const { auth } = getState();
    const { accessToken, clientTimeOffsetMs } = auth;
    if (accessToken) {
      // Date.now() may be wrong if user has wrong time configured,
      // but we know the difference (clientTimeOffsetMs) so we can simply calculate the right time
      if (!isAccessTokenExpired(accessToken, Date.now() - clientTimeOffsetMs)) {
        return accessToken;
      }
    }

    const tokenRequestData: ITokenRequestData = {
      customer: customerId,
      site: siteId,
    };

    // Tiles will fetch token simultaneously, we will reuse one request for
    if (!tokenFetchPromise) {
      tokenFetchPromise = axios({
        method: "POST",
        url: `${config.endpoints.loginService}/api/me`,
        data: tokenRequestData,
        withCredentials: true,
      });
    }

    let resp: AxiosResponse<ILoginServiceApiMeResponse>;
    try {
      resp = await tokenFetchPromise;
    } finally {
      tokenFetchPromise = null;
    }

    if (!resp.data || !resp.data.accessToken) {
      // Unexpected situation
      // TODO: Alert about unexpected error
      throw new CodeMessageError(
        ERROR_LOGIN_SERVICE_API_SCHEMA_INVALID,
        "No access token found in auth response",
      );
    }
    dispatch(putAuthTokenUpdateFulfilled(resp.data.accessToken));
    return resp.data.accessToken;
  };

export const performSignedRequest: IPerformSignedRequest =
  (
    axiosRequestConfig: AxiosRequestConfig,
    customerId: string,
    siteId: string,
  ) =>
  async (
    dispatch: IGcpAppDispatch,
    getState: () => IStoreState,
    { axios }: IExtraArgument,
  ) => {
    const accessToken = await dispatch(ensureAccessToken(customerId, siteId));
    const params: AxiosRequestConfig = {
      ...axiosRequestConfig,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };
    return axios(params);
  };
